/**
 * Módulo de Subvenciones — tipos y normalización.
 *
 * Los datos vienen de un monitor manual (Excel) donde `estado` y `ambito` son
 * texto libre muy variado ("ABIERTA (fondos residuales)", "Isla de Tenerife
 * (Canarias)"...). Aquí normalizamos SOLO para filtrar/etiquetar; el dato
 * original siempre se muestra tal cual.
 */

import type { RequisitosJson } from "./grantEligibility";

export interface Subvencion {
  codigo: string;
  titulo: string;
  organismo: string | null;
  ambito: string | null;
  beneficiarios: string | null;
  importe: string | null;
  presupuesto: string | null;
  fecha_inicio: string | null;
  fecha_fin: string | null;
  modalidad: string | null;
  periodo_gastos: string | null;
  condiciones: string | null;
  hasta_agotar: string | null;
  estado: string | null;
  enlace: string | null;
  categoria: string | null;
  notas: string | null;
  fecha_verificacion: string | null;
  fuente: string | null;
  sectores: string | null;
  /** Frase de valor comercial, SOLO para la vista pública. */
  claim_publico?: string | null;
  /** Requisitos estructurados de ESTA convocatoria (nunca plantilla de otra). */
  requisitos_json?: RequisitosJson | null;
  activa?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export const SUBVENCION_FIELDS =
  "codigo, titulo, organismo, ambito, beneficiarios, importe, presupuesto, fecha_inicio, fecha_fin, modalidad, periodo_gastos, condiciones, hasta_agotar, estado, enlace, categoria, notas, fecha_verificacion, fuente, sectores, requisitos_json, created_at, updated_at";

/** Campos que NUNCA se envían al cliente en la vista pública (gating real). */
export const SUBVENCION_PUBLIC_FIELDS =
  "codigo, titulo, organismo, ambito, beneficiarios, importe, presupuesto, modalidad, hasta_agotar, estado, categoria, fuente, sectores, claim_publico, created_at, updated_at";

export type EstadoGrupo = "abierta" | "proxima" | "cerrada" | "consultar";

/** Agrupa el texto libre de `estado` en 4 grupos filtrables. */
export const estadoGrupo = (estado?: string | null): EstadoGrupo => {
  const s = (estado ?? "").toUpperCase();
  if (!s) return "consultar";
  if (s.includes("FUTURA") || s.includes("PRÓXIMA") || s.includes("PROXIMA")) return "proxima";
  if (s.includes("ABIERTA") || s.includes("ACTIVA") || s.includes("INMINENTE") || s.includes("PARCIALMENTE")) return "abierta";
  if (s.includes("CERRADA")) return "cerrada";
  return "consultar";
};

export const estadoLabel = (grupo: EstadoGrupo, lang: "es" | "en"): string => {
  const map: Record<EstadoGrupo, { es: string; en: string }> = {
    abierta: { es: "Abierta", en: "Open" },
    proxima: { es: "Próxima", en: "Upcoming" },
    cerrada: { es: "Cerrada", en: "Closed" },
    consultar: { es: "Consultar", en: "Check status" },
  };
  return map[grupo][lang];
};

/** Comunidades autónomas + ámbitos supra-regionales usados en los filtros. */
export const COMUNIDADES = [
  "Andalucía",
  "Aragón",
  "Asturias",
  "Canarias",
  "Cantabria",
  "Castilla-La Mancha",
  "Castilla y León",
  "Cataluña",
  "Comunidad de Madrid",
  "Comunidad Foral de Navarra",
  "Comunidad Valenciana",
  "Extremadura",
  "Galicia",
  "Illes Balears",
  "La Rioja",
  "Murcia",
  "País Vasco",
] as const;

const norm = (v: string) =>
  v
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/** Un ámbito nacional o europeo aplica a cualquier comunidad. */
export const isAmbitoGlobal = (ambito?: string | null): boolean => {
  const a = norm(ambito ?? "");
  return a.includes("nacional") || a.includes("union europea") || a.includes("ue)");
};

/** ¿La subvención aplica a la comunidad autónoma indicada? */
export const matchesComunidad = (ambito: string | null | undefined, comunidad?: string | null): boolean => {
  if (!comunidad) return true;
  if (isAmbitoGlobal(ambito)) return true;
  const a = norm(ambito ?? "");
  const c = norm(comunidad);
  if (!a) return false;
  if (a.includes(c) || c.includes(a)) return true;
  // Casos especiales del monitor: islas / denominaciones cortas.
  const alias: Record<string, string[]> = {
    canarias: ["tenerife", "gran canaria", "las palmas"],
    "illes balears": ["baleares", "mallorca", "menorca", "ibiza"],
    "comunidad de madrid": ["madrid"],
    "comunidad foral de navarra": ["navarra"],
    "comunidad valenciana": ["valencia", "valenciana"],
    "pais vasco": ["euskadi", "vasco"],
  };
  return (alias[c] ?? []).some((x) => a.includes(x));
};

/**
 * ¿La subvención está etiquetada para el sector del comercio?
 * Compara etiquetas completas (separadas por "|" o ",") en lugar de subcadenas,
 * para que "Farmacia" no case con "Parafarmacia" ni viceversa.
 */
export const matchesSectorLabel = (sectores: string | null | undefined, sectorLabel?: string | null): boolean => {
  if (!sectorLabel) return true;
  if (!sectores) return false;
  const target = norm(sectorLabel);
  return sectores
    .split(/[|,]/)
    .map((s) => norm(s.trim()))
    .filter(Boolean)
    .includes(target);
};


/** Etiqueta de frescura: nueva (creada <7d) o actualizada esta semana. */
export type Frescura = "nueva" | "actualizada" | null;

export const frescura = (s: Pick<Subvencion, "created_at" | "updated_at">): Frescura => {
  const week = 7 * 24 * 60 * 60 * 1000;
  const now = Date.now();
  const created = s.created_at ? new Date(s.created_at).getTime() : 0;
  const updated = s.updated_at ? new Date(s.updated_at).getTime() : 0;
  if (created && now - created < week) return "nueva";
  if (updated && now - updated < week) return "actualizada";
  return null;
};

/** Lista de sectores etiquetados en la ficha (para chips). */
export const sectoresList = (sectores?: string | null): string[] =>
  (sectores ?? "")
    .split("|")
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * Normaliza el campo `enlace` del monitor, que es TEXTO LIBRE:
 * "juntadeandalucia.es", "BOJA + sede electrónica", "https://x | DOGC"...
 * Devuelve siempre un destino navegable (URL absoluta o búsqueda oficial)
 * para que ningún href quede como ruta relativa.
 */
export interface ResolvedEnlace {
  href: string;
  kind: "official" | "search";
  /** Referencia original tal cual está en el monitor. */
  raw: string | null;
}

const URL_RE = /https?:\/\/[^\s|,)]+/i;
const DOMAIN_RE = /\b((?:[a-z0-9-]+\.)+(?:es|cat|gal|eus|com|org|net|eu|info|madrid|barcelona|gob\.es|europa\.eu))\b/i;

export const resolveEnlace = (
  enlace: string | null | undefined,
  ctx: { titulo?: string | null; organismo?: string | null } = {},
): ResolvedEnlace => {
  const raw = (enlace ?? "").trim() || null;

  if (raw) {
    const url = raw.match(URL_RE)?.[0];
    if (url) return { href: url.replace(/[.,;]+$/, ""), kind: "official", raw };
    const domain = raw.match(DOMAIN_RE)?.[1];
    if (domain) return { href: `https://${domain.toLowerCase()}`, kind: "official", raw };
  }

  const query = [ctx.organismo, ctx.titulo, raw].filter(Boolean).join(" ").slice(0, 220);
  return {
    href: `https://www.google.com/search?q=${encodeURIComponent(query || "convocatoria subvención comercio")}`,
    kind: "search",
    raw,
  };
};

/* ------------------------------------------------------------------ *
 * Priorización, urgencia y tipo de gasto
 *
 * El monitor es texto libre, así que todo se deriva parseando importes,
 * fechas y palabras clave. Estos helpers se comparten entre la vista
 * pública (badges) y el Club Pro (orden y filtros).
 * ------------------------------------------------------------------ */

/** Parsea "30/09/2026 15:00 h" o "2026-09-30" a Date (o null). */
export const parseFecha = (value?: string | null): Date | null => {
  const raw = (value ?? "").trim();
  if (!raw) return null;
  const dmy = raw.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (dmy) {
    const d = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const ymd = raw.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

/** Días naturales hasta el cierre (negativo si ya pasó, null si no hay fecha). */
export const diasHastaCierre = (s: Pick<Subvencion, "fecha_fin">): number | null => {
  const end = parseFecha(s.fecha_fin);
  if (!end) return null;
  return Math.ceil((end.getTime() - Date.now()) / 86_400_000);
};

/** Mayor importe detectado en el texto libre de `importe` (en euros). */
export const importeMaximo = (importe?: string | null): number | null => {
  const raw = importe ?? "";
  if (!raw) return null;
  const found: number[] = [];
  const re = /(\d{1,3}(?:[.\s]\d{3})+|\d+(?:,\d+)?)\s*(millones|millón|m€|€|euros)?/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw))) {
    const n = Number((m[1] ?? "").replace(/[.\s]/g, "").replace(",", "."));
    if (!Number.isFinite(n)) continue;
    const unit = (m[2] ?? "").toLowerCase();
    found.push(unit.startsWith("mill") || unit === "m€" ? n * 1_000_000 : n);
  }
  if (!found.length) return null;
  return Math.max(...found);
};

/** ¿Se concede por orden de llegada o hasta agotar fondos? */
export const esOrdenLlegada = (s: Pick<Subvencion, "modalidad" | "hasta_agotar" | "condiciones">): boolean => {
  const hay = norm([s.modalidad, s.hasta_agotar, s.condiciones].filter(Boolean).join(" "));
  if (/^\s*(no|n\/a)\s*$/.test(norm(s.hasta_agotar ?? ""))) {
    return /orden de (presentacion|llegada)|concesion directa/.test(hay);
  }
  return (
    /orden de (presentacion|llegada)|concesion directa|hasta agotar|agota|si -|si$|posible/.test(hay) &&
    !/concurrencia competitiva/.test(hay)
  );
};

/** NUEVA: verificada (o creada) hace 15 días o menos. */
export const esNueva = (s: Pick<Subvencion, "fecha_verificacion" | "created_at">): boolean => {
  const limit = 15 * 86_400_000;
  const ver = parseFecha(s.fecha_verificacion);
  if (ver) return Date.now() - ver.getTime() <= limit;
  const created = s.created_at ? new Date(s.created_at).getTime() : 0;
  return !!created && Date.now() - created <= limit;
};

const RETAIL_KEYWORDS = /hosteleria|comercio|restauracion|bar|cafeteria|tienda|retail|local comercial|pyme comercial/;

/**
 * PRIORIDAD ALTA: cumple al menos una condición relevante para el comerciante.
 * (importe alto, cierre inminente, orden de llegada o foco comercio/hostelería)
 */
export const esPrioridadAlta = (s: Subvencion): boolean => {
  if (estadoGrupo(s.estado) === "cerrada") return false;
  const importe = importeMaximo(s.importe);
  if (importe !== null && importe >= 5000 && importe < 1_000_000) return true;
  const dias = diasHastaCierre(s);
  if (dias !== null && dias >= 0 && dias <= 40) return true;
  if (esOrdenLlegada(s)) return true;
  const hay = norm([s.categoria, s.beneficiarios, s.titulo].filter(Boolean).join(" "));
  return RETAIL_KEYWORDS.test(hay);
};

/** Urgencia derivada del cierre, para filtrar en el Club Pro. */
export type Urgencia = "urgente" | "proximo" | "sin_prisa" | "sin_fecha";

export const urgencia = (s: Subvencion): Urgencia => {
  const dias = diasHastaCierre(s);
  if (dias === null) return "sin_fecha";
  if (dias < 0) return "sin_prisa";
  if (dias <= 15) return "urgente";
  if (dias <= 45) return "proximo";
  return "sin_prisa";
};

/** Tipos de gasto derivados de categoría + condiciones + periodo de gastos. */
export type TipoGasto = "equipos" | "personal" | "reformas" | "digitalizacion" | "alquiler" | "otros";

const GASTO_RULES: { tipo: TipoGasto; re: RegExp }[] = [
  { tipo: "equipos", re: /equip|maquinaria|mobiliario|electrodomest|horno|camara|climatiz|vehiculo/ },
  { tipo: "personal", re: /contrat|personal|empleo|salari|cotiza|autonomo|conciliacion/ },
  { tipo: "reformas", re: /reform|obra|rehabilit|acondicionamiento|fachada|accesibilidad|escaparate/ },
  { tipo: "digitalizacion", re: /digital|ecommerce|comercio electronico|web|software|ciberseg|kit digital|tecnolog/ },
  { tipo: "alquiler", re: /alquiler|renta|arrendamiento|local vacio/ },
];

export const tiposGasto = (s: Subvencion): TipoGasto[] => {
  const hay = norm([s.categoria, s.condiciones, s.periodo_gastos, s.titulo].filter(Boolean).join(" "));
  const out = GASTO_RULES.filter((r) => r.re.test(hay)).map((r) => r.tipo);
  return out.length ? out : ["otros"];
};

export type OrdenSubvenciones = "prioridad" | "cierre" | "importe" | "verificacion";

/** Orden por defecto: prioridad alta → cierre más próximo → verificación reciente. */
export const sortSubvenciones = (rows: Subvencion[], orden: OrdenSubvenciones = "prioridad"): Subvencion[] => {
  const cierre = (s: Subvencion) => {
    const d = diasHastaCierre(s);
    return d === null || d < 0 ? Number.MAX_SAFE_INTEGER : d;
  };
  const verif = (s: Subvencion) => parseFecha(s.fecha_verificacion)?.getTime() ?? 0;
  const copy = [...rows];
  copy.sort((a, b) => {
    if (orden === "cierre") return cierre(a) - cierre(b);
    if (orden === "importe") return (importeMaximo(b.importe) ?? 0) - (importeMaximo(a.importe) ?? 0);
    if (orden === "verificacion") return verif(b) - verif(a);
    const pa = esPrioridadAlta(a) ? 0 : 1;
    const pb = esPrioridadAlta(b) ? 0 : 1;
    if (pa !== pb) return pa - pb;
    const ca = cierre(a);
    const cb = cierre(b);
    if (ca !== cb) return ca - cb;
    return verif(b) - verif(a);
  });
  return copy;
};

/* ------------------------------------------------------------------ *
 * Estado temporal para las tarjetas del Panel
 * ------------------------------------------------------------------ */

export type EstadoTemporal = "proxima" | "abierta" | "ultimos_dias" | "cerrada" | "consultar";

/** Días naturales hasta la apertura (negativo si ya abrió, null si no hay fecha). */
export const diasHastaApertura = (s: Pick<Subvencion, "fecha_inicio">): number | null => {
  const start = parseFecha(s.fecha_inicio);
  if (!start) return null;
  return Math.ceil((start.getTime() - Date.now()) / 86_400_000);
};

/**
 * Estado temporal derivado de fechas reales cuando existen (Club Pro) y del
 * texto libre de `estado` como respaldo (vista pública sin fechas).
 */
export const estadoTemporal = (s: Subvencion): EstadoTemporal => {
  const grupo = estadoGrupo(s.estado);
  if (grupo === "cerrada") return "cerrada";
  const apertura = diasHastaApertura(s);
  if (apertura !== null && apertura > 0) return "proxima";
  const cierre = diasHastaCierre(s);
  if (cierre !== null) {
    if (cierre < 0) return "cerrada";
    if (cierre <= 15) return "ultimos_dias";
    return "abierta";
  }
  if (grupo === "proxima") return "proxima";
  if (grupo === "abierta") return "abierta";
  return "consultar";
};

/** Formatea una fecha del monitor (texto libre) como DD/MM/YYYY. */
export const formatFechaCorta = (value?: string | null): string | null => {
  const d = parseFecha(value);
  if (!d) return null;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

/**
 * Importe máximo en euros con unidad EXPLÍCITA en el texto ("12.000 €",
 * "hasta 1,2 millones"). A diferencia de `importeMaximo` (usado para ordenar y
 * priorizar), aquí la moneda es obligatoria: así un texto como "hasta el 75% de
 * la inversión" o un año no se muestran nunca como si fueran euros.
 */
export const importeMaximoEuros = (importe?: string | null): number | null => {
  const raw = importe ?? "";
  if (!raw) return null;
  const found: number[] = [];
  const re = /(\d{1,3}(?:[.\s]\d{3})+|\d+(?:[.,]\d+)?)\s*(millones|millón|millon|m€|€|euros|eur)/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw))) {
    const unit = (m[2] ?? "").toLowerCase();
    const numRaw = m[1] ?? "";
    const isMill = unit.startsWith("mill") || unit === "m€";
    // "1,2 millones" usa la coma como decimal; "12.000 €" usa el punto de miles.
    const n = isMill
      ? Number(numRaw.replace(/\s/g, "").replace(",", "."))
      : Number(numRaw.replace(/[.\s]/g, "").replace(",", "."));
    if (!Number.isFinite(n) || n <= 0) continue;
    found.push(isMill ? n * 1_000_000 : n);
  }
  if (!found.length) return null;
  return Math.max(...found);
};

/** Importe máximo formateado en euros, listo para la tarjeta ("12.000 €"). */
export const importeMaximoLabel = (importe?: string | null, lang: "es" | "en" = "es"): string | null => {
  const max = importeMaximoEuros(importe);
  if (max === null) return null;
  return `${max.toLocaleString(lang === "es" ? "es-ES" : "en-US")} €`;
};


/** ¿La ayuda cubre el 100% de la inversión? */
export const esCoberturaTotal = (s: Pick<Subvencion, "importe" | "condiciones">): boolean =>
  /100\s*%/.test([s.importe, s.condiciones].filter(Boolean).join(" "));

/**
 * Mayor porcentaje financiable detectado en el texto libre ("hasta el 75%",
 * "80 % de la inversión"). Devuelve "75%" o null si no hay porcentaje.
 */
export const porcentajeFinanciable = (s: Pick<Subvencion, "importe" | "condiciones">): string | null => {
  const hay = [s.importe, s.condiciones].filter(Boolean).join(" ");
  const found = [...hay.matchAll(/(\d{1,3})(?:[.,]\d+)?\s*%/g)]
    .map((m) => Number(m[1]))
    .filter((n) => n > 0 && n <= 100);
  if (!found.length) return null;
  return `${Math.max(...found)}%`;
};

/**
 * Desglose de cuantía por beneficiario tal cual figura en el monitor.
 * Nunca incluye `presupuesto` (dotación total del programa), que solo se
 * muestra por separado en el detalle de la ficha.
 */
export const desgloseCuantia = (s: Pick<Subvencion, "importe">): string | null => s.importe || null;

/**
 * Mes/año de la verificación más reciente del monitor. Se deriva de los datos
 * (`fecha_verificacion`), nunca se escribe a mano, así el sello "Actualizado"
 * nunca miente. Si no hay fechas legibles, cae a la fecha actual.
 */
export const ultimaVerificacion = (
  rows: Pick<Subvencion, "fecha_verificacion">[] | null | undefined,
  lang: "es" | "en",
): { month: string; year: number } => {
  const times = (rows ?? [])
    .map((r) => parseFecha(r.fecha_verificacion))
    .filter((d): d is Date => d !== null)
    .map((d) => d.getTime());
  const ref = times.length ? new Date(Math.max(...times)) : new Date();
  const raw = ref.toLocaleString(lang === "es" ? "es-ES" : "en-US", { month: "long" });
  return { month: raw.charAt(0).toUpperCase() + raw.slice(1), year: ref.getFullYear() };
};
